import moment from 'moment';

import { AppointmentConfiguration, AppointmentPractitioner } from '@dreryk/egabinet-appointment-frontend-api';

import { Range } from '@dreryk/watermelon';

import { AppointmentsTimetable, useAppointmentSchedules } from '~libs.shared.appointments-timetable';

import { AppointmentBookModal } from '../../appointment/create/AppointmentBookModal';
import { CalendarController, CalendarSelectedDate, CalendarSelectedDateSetter } from '../../CalendarController.types';
import { CalendarSidePanel } from '../../SidePanel/CalendarSidePanel';
import { MiniCalendar } from '../../SidePanel/MiniCalendar';
import { AppointmentContextDialog } from '../shared/contextDialog/AppointmentContextDialog';
import {
   useCancelledVisibilityPreferenceState,
   useScheduleOnlyVisibilityPreferenceState,
} from '../shared/preferences/preferences.storage';
import { getMinMaxTime } from '../shared/utils/getMinMaxTime';
import { TimetablePlaceholder } from '../shared/TimetablePlaceholder/TimetablePlaceholder';

import { useResources } from './HealthcareLocationTimetable.hooks';
import { PractitionerResourceLabelContent } from './PractitionerResourceLabelContent';
import { HealthcareLocationTimetableActions } from './actions/HealthcareLocationTimetableActions';

type HealthcareLocationTimetableProps = Pick<CalendarController, 'register'> & {
   readonly currentDate: CalendarSelectedDate;
   readonly setCurrentDate: CalendarSelectedDateSetter;
   readonly practitioners: AppointmentPractitioner[];
   readonly appointmentConfiguration: AppointmentConfiguration;
};

export const HealthcareLocationTimetable = (props: HealthcareLocationTimetableProps): JSX.Element => {
   const [preferScheduleOnlyVisible] = useScheduleOnlyVisibilityPreferenceState();
   const [preferCancelledVisitsVisible] = useCancelledVisibilityPreferenceState();
   const { appointmentSchedules, appointmentSchedulesFetching, refetchAppointmentSchedules } = useAppointmentSchedules({
      dateRange: Range.closed(props.currentDate, moment(props.currentDate).add(1, 'day').toDate()),
      practitionerIds: props.practitioners.map((practitioner) => practitioner.id),
   });

   const resources = useResources({
      practitioners: props.practitioners,
      appointmentSchedules: appointmentSchedules,
   });

   const { minTime, maxTime } = getMinMaxTime(
      appointmentSchedules,
      Boolean(preferScheduleOnlyVisible),
      Boolean(preferCancelledVisitsVisible)
   );

   return (
      <>
         <AppointmentsTimetable
            view={'resourceTimeGridDay'}
            resources={resources}
            resourceLabelContent={PractitionerResourceLabelContent}
            resourceMinWidth={207}
            selectedDate={props.currentDate}
            setSelectedDate={props.setCurrentDate}
            appointmentSchedules={appointmentSchedules}
            onEventChange={refetchAppointmentSchedules}
            selectionOverlapping={props.appointmentConfiguration.overlappingAppointmentsAllowed}
            interactive={!appointmentSchedulesFetching}
            preferCancelledVisitsVisible={preferCancelledVisitsVisible}
            slotMinTime={minTime}
            slotMaxTime={maxTime}
            actions={<HealthcareLocationTimetableActions />}
            noEventsPlaceholder={preferScheduleOnlyVisible ? <TimetablePlaceholder showTurnOffLink /> : undefined}
            noResourcesPlaceholder={<TimetablePlaceholder />}
         >
            <AppointmentsTimetable.EventSelection onUnselect={refetchAppointmentSchedules}>
               {({ selectedEvent, selectedEventElement, unselectEvent }) => (
                  <AppointmentContextDialog
                     triggerElement={selectedEventElement}
                     event={selectedEvent}
                     onEventChange={unselectEvent}
                  />
               )}
            </AppointmentsTimetable.EventSelection>

            <AppointmentsTimetable.SlotSelection onUnselect={refetchAppointmentSchedules}>
               {({ selectedSlot, unselectSlot }) => (
                  <AppointmentBookModal
                     selectedSlot={selectedSlot}
                     onClose={unselectSlot}
                     onSuccess={unselectSlot}
                     overlappingAppointmentsAllowed={props.appointmentConfiguration.overlappingAppointmentsAllowed}
                  />
               )}
            </AppointmentsTimetable.SlotSelection>
         </AppointmentsTimetable>

         <CalendarSidePanel.Portal placement={'miniCalendar'}>
            <MiniCalendar currentDate={props.currentDate} onDateChange={props.setCurrentDate} />
         </CalendarSidePanel.Portal>
      </>
   );
};
